/**
 * clickable-brand-chooser.js -- brand-page drill-down vehicle browser (Task 07).
 * Canon: docs/design/components/PageBrand.jsx (SlimStepper + OptionTile grid).
 * Data: window.clickableBrandChooserData (clickable-brand-chooser-data.js).
 *
 * Levels: make (pick series) -> series (pick chassis) -> chassis (pick model).
 * Model click is TERMINAL (live parity): writes canonical state through
 * window.clickableVehicleState and navigates to /collections/store/{tag}.
 *
 * URL ruling v3 (Lucas, 03 Aug -- supersedes v2): legacy TAG URLs are the
 * canonical, indexable surface -- step options render as real <a href> to
 * their tag URLs (crawlable graph, middle-click safe), JS-intercepted for
 * the in-place transition. ?path= is EPHEMERAL UI state written by
 * pushState mid-flow; rel=canonical is repointed at the nearest tag URL
 * every render (brand root when none). M-series keys (M2..M8) have no
 * product tag -- their step href falls back to the ?path= form and their
 * canonical is the chassis-tag page / brand root. No scroll-to-top.
 *
 * Single-option auto-advance (Lucas ruling, 03 Aug): fires ONLY on
 * forward user-driven progression (never on load, popstate, or
 * back-pointing links) -- a step with exactly one option auto-selects,
 * chaining to terminal model completion when the chain bottoms out.
 *
 * Loaded WITHOUT defer immediately after the section markup so the first
 * render happens during HTML parse -- zero layout shift on the tile grid.
 * Never writes vehicle localStorage keys directly; the one direct READ at
 * boot follows the clickable-apps precedent (state module is deferred).
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-cbc-root]');
  if (!root || window.clickableBrandChooserBound) return;
  window.clickableBrandChooserBound = true;

  var BRAND = root.getAttribute('data-cbc-brand');
  var DATA = (window.clickableBrandChooserData || {})[BRAND] || {};
  var config = {};
  try {
    config = JSON.parse(document.getElementById('cbc-config').textContent);
  } catch (error) {
    config = {};
  }

  var els = {
    eyebrow: root.querySelector('[data-cbc-eyebrow]'),
    h1: root.querySelector('[data-cbc-h1]'),
    lede: root.querySelector('[data-cbc-lede]'),
    crumbs: root.querySelector('[data-cbc-crumbs]'),
    stepper: root.querySelector('[data-cbc-stepper]'),
    browsing: root.querySelector('[data-cbc-browsing]'),
    pickHeading: root.querySelector('[data-cbc-pick-heading]'),
    pickHelper: root.querySelector('[data-cbc-pick-helper]'),
    grid: root.querySelector('[data-cbc-grid]'),
    deep: root.querySelector('[data-cbc-deep]'),
    infobox: root.querySelector('[data-cbc-infobox]'),
  };

  var nativeTitle = document.title;
  // Base collection path: legacy inbound tag URLs (/collections/bmw/{tag}+{tag})
  // carry a third segment - step URLs are always built on the base.
  var BASE_PATH = config.collectionUrl || window.location.pathname.split('/').slice(0, 3).join('/');

  function slug(value) {
    return value.toLowerCase().replace(/[ ./]/g, '-');
  }

  // Tag -> URL segment per the vehicle-state contract ([ ./] -> '-'),
  // LOWERCASED: Shopify's native canonical_url emits the lowercased form
  // (verified on the draft, 03 Aug) - emitting it byte-identical keeps the
  // crawlable graph == the canonical surface. Tag matching is handle-based,
  // so case never affects resolution.
  function slugTag(tag) {
    return encodeURIComponent(tag.replace(/[ ./]/g, '-').toLowerCase());
  }

  // Append the current non-path query params (preview/campaign) to a path;
  // 'path' itself never rides a tag URL - the pathname expresses the state.
  function withParams(path) {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return path;
    }
    params.delete('path');
    var qs = params.toString();
    return path + (qs ? '?' + qs : '');
  }

  // The REAL product chassis tag ('F87 M (2016 - 2020)') derived from the
  // first model's full vehicle name - the data key alone drops the ' M'
  // suffix the rehomed M-series keys shed (v3 data note).
  function chassisTagFor(seriesKey, chassisKey) {
    var models = DATA[seriesKey].chassis[chassisKey].models;
    if (!models.length) return chassisKey;
    var m = models[0].name.match(/^(?:BMW|Volkswagen|Audi)\s+(.+?)\s+\(([^)]*)\)/);
    if (!m) return chassisKey;
    var sc = m[1];
    var code = sc.indexOf(seriesKey + ' ') === 0
      ? sc.slice(seriesKey.length + 1)
      : sc.replace(/^\d Series /, '').replace(/^[XZ]\d /, '');
    return code + ' (' + m[2] + ')';
  }

  // Canonical tag URL for a selection, or null where none exists (M-series
  // keys are generator constructs, not product tags).
  function tagUrlFor(sel) {
    if (!sel.series) return BASE_PATH;
    // M-series keys ('M3') and the v3 X/Z-M promotions ('X5 M') are
    // generator constructs, not product tags - no series tag URL exists.
    var seriesIsTag = !/^M\d$/.test(sel.series) && !/ M$/.test(sel.series);
    if (!sel.chassis) {
      return seriesIsTag ? BASE_PATH + '/' + slugTag(sel.series) : null;
    }
    var chassisSeg = slugTag(chassisTagFor(sel.series, sel.chassis));
    if (seriesIsTag) {
      return BASE_PATH + '/' + slugTag(sel.series) + '+' + chassisSeg;
    }
    return BASE_PATH + '/' + chassisSeg;
  }

  function esc(value) {
    var div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  function fill(template, ctx) {
    return (template || '').replace(/\[\[(\w+)\]\]/g, function (_, key) {
      return ctx[key] !== undefined ? ctx[key] : '';
    });
  }

  // Canonical store URL for a model entry -- encoding convention matches the
  // state module's canonical-redirect builder (encodeURIComponent, '/' kept).
  function storeUrl(tag) {
    return '/collections/store/' + encodeURIComponent(tag).replace(/%2F/gi, '/');
  }

  // ---- Selection from ?path= --------------------------------------------
  function readSelection() {
    var sel = { series: null, chassis: null };
    var path = null;
    try {
      path = new URL(window.location.href).searchParams.get('path');
    } catch (error) { /* no URL API -> make level */ }
    if (!path) return sel;
    var parts = path.split('/').filter(Boolean);
    if (!parts.length) return sel;

    var seriesKey = Object.keys(DATA).filter(function (s) {
      return slug(s) === parts[0];
    })[0];
    if (!seriesKey) return sel;
    sel.series = seriesKey;

    if (parts[1]) {
      var chassisKey = Object.keys(DATA[seriesKey].chassis).filter(function (c) {
        return slug(c) === parts[1];
      })[0];
      if (chassisKey) sel.chassis = chassisKey;
    }
    return sel;
  }

  function hrefFor(sel) {
    var parts = [];
    if (sel.series) parts.push(slug(sel.series));
    if (sel.chassis) parts.push(slug(sel.chassis));
    // Preserve non-path query params (?view=brand during preview testing,
    // campaign params in production) so step navigation never drops them.
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      params = null;
    }
    if (params) {
      if (parts.length) params.set('path', parts.join('/'));
      else params.delete('path');
      var qs = params.toString().replace(/%2F/gi, '/');
      return BASE_PATH + (qs ? '?' + qs : '');
    }
    if (!parts.length) return BASE_PATH;
    return BASE_PATH + '?path=' + encodeURIComponent(parts.join('/')).replace(/%2F/gi, '/');
  }

  function levelOf(sel) {
    if (sel.chassis) return 'chassis';
    if (sel.series) return 'series';
    return 'make';
  }

  // ---- Derived display helpers -------------------------------------------
  function chassisYears(seriesKey, chassisKey) {
    var models = DATA[seriesKey].chassis[chassisKey].models;
    return models.length ? models[0].range : '';
  }

  // Legacy multi-tag inbound aliases (Delta 9 / URL parity ruling v2):
  // /collections/{brand}/{series-tag}+{chassis-tag} URLs advance the chooser.
  // Series tag = exact series key; chassis tag = '{code} ({range})' whose code
  // part matches a chassis key. Vehicle tags never match here (no series/
  // chassis key contains '(' or a brand prefix), and the resolver handles them.
  function selectionFromTags(tags) {
    if (!tags || !tags.length) return null;
    var sel = { series: null, chassis: null };
    var seriesKeys = Object.keys(DATA);
    var i, s;
    for (i = 0; i < tags.length && !sel.series; i++) {
      for (s = 0; s < seriesKeys.length; s++) {
        if (seriesKeys[s].toLowerCase() === String(tags[i]).toLowerCase()) {
          sel.series = seriesKeys[s];
          break;
        }
      }
    }
    for (i = 0; i < tags.length && !sel.chassis; i++) {
      var tag = String(tags[i]);
      if (tag.indexOf('(') === -1) continue;
      var code = tag.split(' (')[0].toLowerCase();
      // Legacy M-chassis tags carry a ' M' suffix the v3 keys dropped when
      // M-models became their own series ('F87 M (2016 - 2020)' -> key 'F87').
      var codeNoM = code.replace(/ m$/, '');
      // Series-scoped first; on miss, widen to ALL series and let the chassis
      // match override the series - legacy '2 Series + F87 M' tags must land
      // on M2 > F87, where v3 rehomed the dedicated-chassis M-models.
      var pools = sel.series ? [[sel.series], seriesKeys] : [seriesKeys];
      for (var pi = 0; pi < pools.length && !sel.chassis; pi++) {
        var pool = pools[pi];
        for (s = 0; s < pool.length && !sel.chassis; s++) {
          var chassisKeys = Object.keys(DATA[pool[s]].chassis);
          for (var c = 0; c < chassisKeys.length; c++) {
            var key = chassisKeys[c].toLowerCase();
            if (key === code || key === codeNoM) {
              sel.series = pool[s];
              sel.chassis = chassisKeys[c];
              break;
            }
          }
        }
      }
    }
    return sel.series ? sel : null;
  }

  function findVehicle(name) {
    var seriesKeys = Object.keys(DATA);
    for (var i = 0; i < seriesKeys.length; i++) {
      var chassisMap = DATA[seriesKeys[i]].chassis;
      var chassisKeys = Object.keys(chassisMap);
      for (var j = 0; j < chassisKeys.length; j++) {
        var models = chassisMap[chassisKeys[j]].models;
        for (var k = 0; k < models.length; k++) {
          if (models[k].name.toLowerCase() === name.toLowerCase()) {
            return { series: seriesKeys[i], chassis: chassisKeys[j], model: models[k] };
          }
        }
      }
    }
    return null;
  }

  // ---- Renderers ----------------------------------------------------------
  // Rule 19: explicit width/height attrs on every JS-emitted SVG.
  var CHEVRON = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var TICK = '<svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // Step-link attributes: the href is the CANONICAL tag URL when one exists
  // (crawlable graph + middle-click), else the ephemeral ?path= form; the
  // data attrs carry the selection for in-place interception either way.
  function stepAttrs(sel) {
    var href = tagUrlFor(sel);
    var attrs = ' href="' + (href ? withParams(href) : hrefFor(sel)) + '"';
    attrs += ' data-cbc-sel-series="' + (sel.series ? esc(sel.series) : '') + '"';
    attrs += ' data-cbc-sel-chassis="' + (sel.chassis ? esc(sel.chassis) : '') + '"';
    return attrs;
  }

  function cardTreatment() {
    try {
      return new URL(window.location.href).searchParams.get('cardtreat') || 'a';
    } catch (error) {
      return 'a';
    }
  }

  function tile(options) {
    var classes = 'cbc__tile' + (options.active ? ' cbc__tile--active' : '') +
      (options.variant ? ' cbc__tile--' + options.variant : '');
    var html = '<a class="' + classes + '"';
    if (options.sel) {
      html += stepAttrs(options.sel);
    } else {
      html += ' href="' + options.href + '"';
    }
    if (options.data) {
      Object.keys(options.data).forEach(function (key) {
        html += ' data-' + key + '="' + esc(options.data[key]) + '"';
      });
    }
    html += '><div><div class="cbc__tile-top"><span class="cbc__tile-title">' + esc(options.title) + '</span>';
    if (options.coprimary) {
      html += '<span class="cbc__tile-coprimary">' + esc(options.coprimary) + '</span>';
    }
    if (options.badge) {
      html += '<span class="cbc__tile-badge' + (options.badgeFit ? ' cbc__tile-badge--fit' : '') + '">' + esc(options.badge) + '</span>';
    }
    html += '</div>';
    if (options.secondary) {
      html += '<div class="cbc__tile-secondary">' + esc(options.secondary) + '</div>';
    }
    if (options.expansion) {
      html += '<div class="cbc__tile-expansion">' + esc(options.expansion) + '</div>';
    }
    html += '</div>';
    if (options.hint) {
      html += '<div class="cbc__tile-hint">' + esc(options.hint) + '</div>';
    }
    html += '<div class="cbc__tile-action">' + esc(options.action || 'View parts') + ' ' + CHEVRON + '</div></a>';
    return html;
  }

  // Counters amendment (Delta 9): the chooser displays NO inventory counts at
  // any step - qualitative content only (names, ranges, chassis codes). The
  // data file keeps counts internally for ordering/most_stocked.
  function renderTiles(sel, activeName) {
    var level = levelOf(sel);
    var html = '';

    if (level === 'make') {
      Object.keys(DATA).forEach(function (seriesKey) {
        var chassisMap = DATA[seriesKey].chassis;
        var codes = Object.keys(chassisMap);
        html += tile({
          title: seriesKey,
          hint: codes.slice(0, 4).join(' · ') + (codes.length > 4 ? '…' : ''),
          sel: { series: seriesKey, chassis: null },
          // Delta 10 B: qualitative merchandising signal, exempt from the
          // no-counts rule (Lucas's default, veto open). Data-driven.
          badge: DATA[seriesKey].most_stocked === true ? 'Most stocked' : null,
        });
      });
    } else if (level === 'series') {
      // EXPERIMENT (Lucas design item, 03 Aug): date-hierarchy treatments,
      // flip-able via ?cardtreat= (FID-03x protocol; loser deleted, winner
      // becomes the canon card amendment). Everyday owners navigate by
      // YEAR - the build range moves toward co-primary; chassis code (+ the
      // future item-6 expansion line slot) is the enthusiast layer.
      //   a (default) - baseline: code primary, range secondary
      //   b - range primary, code as the enthusiast sub-line
      //   c - code and range co-primary on one line
      var treat = cardTreatment();
      // Chassis display map (item 6, Lucas-authored): per-(series, chassis)
      // AU-delivered code/body expansions - DISPLAY layer only, the
      // enthusiast line under the code; tags/URLs/state stay canonical.
      var expMap = ((window.clickableChassisDisplayMap || {})[BRAND] || {})[sel.series] || {};
      Object.keys(DATA[sel.series].chassis).forEach(function (chassisKey) {
        var entry = DATA[sel.series].chassis[chassisKey];
        var names = entry.models.map(function (m) { return m.model; });
        var years = chassisYears(sel.series, chassisKey);
        var opts = {
          expansion: expMap[chassisKey] || null,
          hint: names.slice(0, 3).join(', ') + (names.length > 3 ? '…' : ''),
          sel: { series: sel.series, chassis: chassisKey },
        };
        if (treat === 'b') {
          opts.title = years;
          opts.secondary = chassisKey; // item-6 expansion line appends here
          opts.variant = 'treat-b';
        } else if (treat === 'c') {
          opts.title = chassisKey;
          opts.coprimary = years;
          opts.variant = 'treat-c';
        } else {
          opts.title = chassisKey;
          opts.secondary = years;
        }
        html += tile(opts);
      });
    } else {
      DATA[sel.series].chassis[sel.chassis].models.forEach(function (model) {
        var isActive = activeName && model.name.toLowerCase() === activeName.toLowerCase();
        html += tile({
          title: model.model,
          href: storeUrl(model.tag),
          badge: isActive ? 'Your car' : null,
          badgeFit: isActive,
          active: isActive,
          data: { 'cbc-model-name': model.name, 'cbc-model-tag': model.tag },
        });
      });
    }

    els.grid.innerHTML = html;
  }

  function renderStepper(sel, done) {
    var chassisLabel = sel.chassis || null;
    var steps = [
      { label: 'Make', value: BRAND, sel: { series: null, chassis: null }, state: 'done' },
      { label: 'Series', value: sel.series, sel: sel.series ? { series: sel.series, chassis: null } : null },
      { label: 'Chassis', value: chassisLabel, sel: sel.chassis ? sel : null },
      { label: 'Model', value: done ? done.model.model : null, sel: null },
    ];
    steps.forEach(function (step, i) {
      if (i === 0) return;
      if (step.value) {
        step.state = 'done';
      } else if (steps[i - 1].value) {
        step.state = 'current';
      } else {
        step.state = 'locked';
      }
    });

    // PERSISTENT structure, mutated in place - innerHTML rebuilds recreate
    // the elements and the sec-41 token transitions (dot colour, connector
    // fill) never fire. Built once (replacing the server skeleton with the
    // identical composition - zero shift), class-swapped per step change.
    if (!els.stepper.hasAttribute('data-cbc-stepper-live')) {
      var html = '';
      steps.forEach(function (step, i) {
        html += '<a class="cbc__step"><span class="cbc__step-dot"></span>' +
          '<span class="cbc__step-text"><span class="cbc__step-label">' + step.label + '</span>' +
          '<span class="cbc__step-value"></span></span></a>';
        if (i < steps.length - 1) html += '<span class="cbc__step-conn" aria-hidden="true"></span>';
      });
      els.stepper.innerHTML = html;
      els.stepper.setAttribute('data-cbc-stepper-live', '');
    }
    var nodes = els.stepper.querySelectorAll('.cbc__step');
    var conns = els.stepper.querySelectorAll('.cbc__step-conn');
    steps.forEach(function (step, i) {
      var node = nodes[i];
      node.className = 'cbc__step cbc__step--' + step.state;
      var dot = node.querySelector('.cbc__step-dot');
      var dotContent = step.state === 'done' ? TICK : String(i + 1);
      if (dot.getAttribute('data-cbc-dot') !== dotContent) {
        dot.innerHTML = dotContent;
        dot.setAttribute('data-cbc-dot', dotContent);
      }
      node.querySelector('.cbc__step-value').textContent =
        step.state === 'done' ? step.value : step.state === 'current' ? 'Choose yours' : '—';
      if (step.state === 'done' && step.sel) {
        var href = tagUrlFor(step.sel);
        node.setAttribute('href', href ? withParams(href) : hrefFor(step.sel));
        node.setAttribute('data-cbc-sel-series', step.sel.series || '');
        node.setAttribute('data-cbc-sel-chassis', step.sel.chassis || '');
        node.setAttribute('data-cbc-nav', '');
      } else {
        node.removeAttribute('href');
        node.removeAttribute('data-cbc-sel-series');
        node.removeAttribute('data-cbc-sel-chassis');
        node.removeAttribute('data-cbc-nav');
      }
      if (i < steps.length - 1) {
        var active = step.state === 'done' && steps[i + 1].state !== 'locked';
        conns[i].className = 'cbc__step-conn' + (active ? ' cbc__step-conn--active' : '');
      }
    });
  }

  function renderCrumbs(sel) {
    var sep = '<span class="cbc__breadcrumb-sep" aria-hidden="true">&rsaquo;</span>';
    var html = '<a href="/">Home</a>' + sep;
    if (sel.series) {
      html += '<a' + stepAttrs({ series: null, chassis: null }) + ' data-cbc-nav>' + esc(BRAND) + '</a>' + sep;
      if (sel.chassis) {
        html += '<a' + stepAttrs({ series: sel.series, chassis: null }) + ' data-cbc-nav>' + esc(sel.series) + '</a>' + sep;
        html += '<span aria-current="page">' + esc(sel.chassis) + '</span>';
      } else {
        html += '<span aria-current="page">' + esc(sel.series) + '</span>';
      }
    } else {
      html += '<span aria-current="page">' + esc(BRAND) + '</span>';
    }
    els.crumbs.innerHTML = html;
  }

  function renderCopy(sel) {
    var level = levelOf(sel);
    var ctx = {
      make: BRAND,
      series: sel.series || '',
      chassis: sel.chassis || '',
      years: sel.chassis ? chassisYears(sel.series, sel.chassis) : '',
    };
    var copy = config.levels[level] || {};

    if (level === 'make') {
      els.eyebrow.setAttribute('hidden', '');
      els.h1.textContent = config.brand.h1;
      /* lede is server-rendered HTML from the same Liquid capture the
         markup uses (one owner) -- restore it verbatim */
      els.lede.innerHTML = config.brand.lede;
      els.deep.innerHTML = config.brand.deep;
      els.deep.removeAttribute('hidden');
    } else {
      els.eyebrow.textContent =
        level === 'series' ? BRAND + ' · Series' : BRAND + ' · ' + sel.series;
      els.eyebrow.removeAttribute('hidden');
      els.h1.textContent = fill(copy.h1, ctx);
      els.lede.innerHTML = '<p>' + esc(fill(copy.lede, ctx)) + '</p>';
      var body = fill(copy.body, ctx);
      if (body) {
        els.deep.innerHTML = '<div class="cbc__deep-blocks"><p>' + esc(body) + '</p></div>';
        els.deep.removeAttribute('hidden');
      } else {
        els.deep.setAttribute('hidden', '');
      }
    }
    els.pickHeading.textContent = fill(copy.pickHeading, ctx);
    /* pickHelper is server-rendered HTML from the same Liquid capture the
       markup uses (one owner). Lucas, 25 Aug: at the SERIES level the same
       node seats BELOW the card grid (position change, not a new slot);
       make/chassis keep it under the pick heading. */
    els.pickHelper.innerHTML = fill(copy.pickHelper, ctx);
    if (level === 'series' || level === 'chassis') {
      if (els.pickHelper.previousElementSibling !== els.grid) els.grid.after(els.pickHelper);
    } else if (els.pickHelper.parentElement !== els.pickHeading.parentElement) {
      els.pickHeading.parentElement.appendChild(els.pickHelper);
    }
    // Native document title (Shopify SEO title) is kept at make level; only
    // client-side deeper levels retitle, and returning restores the original.
    // Assembled human-readable form, pipe separator = parity with the
    // server-rendered tag-URL titles (clickable-collection-meta mirror).
    if (level === 'make') {
      document.title = nativeTitle;
    } else {
      document.title = fill(copy.h1, ctx).replace(/\s+/g, ' ') + ' | ' + config.shopName;
    }
  }

  // URL ruling v3: rel=canonical follows the step - the nearest tag URL,
  // brand root when the selection has none (M-series mid-flow).
  var canonicalOrigin = null; // Shopify's own canonical origin (vanity
  // domain in production, preserved from the server-emitted link).
  function updateCanonical(sel) {
    var link = document.querySelector('link[rel="canonical"]');
    if (!link) return;
    if (canonicalOrigin === null) {
      try {
        canonicalOrigin = new URL(link.href).origin;
      } catch (error) {
        canonicalOrigin = window.location.origin;
      }
    }
    // tagUrlFor is null only for a chassis-less M-series selection - its
    // nearest tag URL is the brand root.
    var target = tagUrlFor(sel) || BASE_PATH;
    link.setAttribute('href', canonicalOrigin + target);
  }

  function renderInfobox(sel) {
    var facts = sel.chassis ? config.chassisFacts[sel.chassis] : null;
    if (!facts) {
      els.infobox.setAttribute('hidden', '');
      return;
    }
    var rows = [
      ['Build years', facts.years],
      ['Body styles', facts.bodies],
      ['Engines', facts.engines],
      ['Common wear', facts.common],
    ];
    var html = '<div class="cbc__infobox-label">' + esc(sel.chassis) + ' · at a glance</div><div class="cbc__infobox-grid">';
    rows.forEach(function (row) {
      if (!row[1]) return;
      html += '<div class="cbc__infobox-item"><div class="cbc__infobox-key">' + row[0] + '</div><div class="cbc__infobox-value">' + esc(row[1]) + '</div></div>';
    });
    els.infobox.innerHTML = html + '</div>';
    els.infobox.removeAttribute('hidden');
  }

  function renderBrowsingBanner(vehicle) {
    if (!vehicle) {
      els.browsing.setAttribute('hidden', '');
      return;
    }
    els.browsing.innerHTML =
      '<span class="cbc__browsing-text">Browsing your <strong>' + esc(vehicle.model.name) + '</strong></span>' +
      '<a class="cbc__browsing-cta" href="' + storeUrl(vehicle.model.tag) + '">View parts for your ' + esc(vehicle.model.model) + '</a>';
    els.browsing.removeAttribute('hidden');
  }

  // ---- Boot state: a vehicle of THIS brand already set? -------------------
  // Direct read at parse time (clickable-apps precedent: the state module is
  // deferred and not yet loaded); all WRITES go through the module at click time.
  function activeVehicleName() {
    try {
      var name = localStorage.getItem('activeVehicleName');
      return name === null ? null : name.replace(/"/g, '');
    } catch (error) {
      return null;
    }
  }

  // Category-link decoration (Delta 9): server hrefs stay canonical-generic
  // (SEO-intentional); the client scopes them progressively - vehicle tag when
  // set, else the chassis TAG mid-drill, else the bare brand tag. Recomputed on
  // every render, so a reset strips back down the ladder. Href-only: zero shift.
  function activeVehicleTagRaw() {
    try {
      var tag = localStorage.getItem('activeVehicleTag');
      return tag === null ? null : tag.replace(/"/g, '');
    } catch (error) {
      return null;
    }
  }

  function decorateCats(sel) {
    var scope;
    var vehicleTag = activeVehicleTagRaw();
    if (vehicleTag) {
      scope = encodeURIComponent(vehicleTag).replace(/%2F/gi, '/');
    } else if (sel.chassis) {
      // Real product chassis tag (' M' forms included) - the key alone
      // under-reconstructs rehomed M-chassis (same derivation as tagUrlFor).
      scope = slugTag(chassisTagFor(sel.series, sel.chassis));
    } else {
      scope = encodeURIComponent(BRAND);
    }
    root.querySelectorAll('[data-cbc-cat]').forEach(function (link) {
      var base = link.getAttribute('data-cbc-cat');
      if (!base || base.indexOf('/collections/') !== 0) return;
      link.setAttribute('href', base + '/' + scope);
    });
  }

  function render(sel) {
    var name = activeVehicleName();
    var active = name && name.toUpperCase().indexOf(BRAND.toUpperCase()) === 0 ? findVehicle(name) : null;
    renderCrumbs(sel);
    renderStepper(sel, active && sel.chassis === active.chassis && sel.series === active.series ? active : null);
    renderCopy(sel);
    renderTiles(sel, active ? active.model.name : null);
    renderInfobox(sel);
    renderBrowsingBanner(active);
    decorateCats(sel);
    updateCanonical(sel);
  }

  // ---- Arrive-with-vehicle: pre-scope the drill to the active car ---------
  // Kit-specced state (flagged for confirmation in the Task 07 report): the
  // stepper's own "all set" state + a browsing shortcut into store/{tag}.
  var selection = readSelection();

  // Legacy inbound alias boot (URL ruling v3): no ?path= but series/chassis
  // tags in the URL -> pre-advance in place. The tag URL itself is the
  // canonical surface, so the address is KEPT - unless the landed variant
  // differs from the canonical tag form (' M' chassis codes, tag order,
  // chassis-only), which replaceState-normalises to canonical.
  if (levelOf(selection) === 'make') {
    var tagSelection = selectionFromTags(config.currentTags);
    if (tagSelection) {
      selection = tagSelection;
      var canonicalPath = tagUrlFor(selection);
      if (canonicalPath && window.location.pathname !== canonicalPath) {
        try {
          window.history.replaceState({}, '', withParams(canonicalPath));
        } catch (error) { /* render still correct on the landed URL */ }
      }
    }
  }

  var bootName = activeVehicleName();
  if (levelOf(selection) === 'make' && bootName && bootName.toUpperCase().indexOf(BRAND.toUpperCase()) === 0) {
    var bootVehicle = findVehicle(bootName);
    if (bootVehicle) {
      selection = { series: bootVehicle.series, chassis: bootVehicle.chassis };
      try {
        window.history.replaceState({}, '', hrefFor(selection));
      } catch (error) { /* history unavailable -- render still correct */ }
    }
  }
  render(selection);

  // ---- Navigation ----------------------------------------------------------
  // Single-option auto-advance (Lucas ruling, 03 Aug): forward user-driven
  // progression only. Chains: one chassis -> advance; one model -> TERMINAL
  // (state write + store navigation). Returns the deepened selection, or
  // the terminal model entry.
  function resolveForward(sel) {
    if (sel.series && !sel.chassis) {
      var chassisKeys = Object.keys(DATA[sel.series].chassis);
      if (chassisKeys.length === 1) sel = { series: sel.series, chassis: chassisKeys[0] };
    }
    if (sel.series && sel.chassis) {
      var models = DATA[sel.series].chassis[sel.chassis].models;
      if (models.length === 1) return { terminal: models[0] };
    }
    return { sel: sel };
  }

  function depthOf(sel) {
    return sel.chassis ? 2 : sel.series ? 1 : 0;
  }

  root.addEventListener('click', function (event) {
    var link = event.target.closest('a');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

    // Terminal: model tile -> write canonical state, follow href (live parity).
    var modelName = link.getAttribute('data-cbc-model-name');
    if (modelName) {
      if (window.clickableVehicleState) {
        window.clickableVehicleState.setVehicle(modelName, link.getAttribute('data-cbc-model-tag'));
      }
      return; // native navigation to /collections/store/{tag}
    }

    // Step link (tile / stepper / crumb): intercept for the in-place
    // transition - pushState the ephemeral ?path= form, re-render. The
    // held scroll position is deliberate (v3: the scroll-to-top dies).
    if (!link.hasAttribute('data-cbc-sel-series')) return;
    event.preventDefault();
    var current = readSelection();
    var target = {
      series: link.getAttribute('data-cbc-sel-series') || null,
      chassis: link.getAttribute('data-cbc-sel-chassis') || null,
    };

    // Auto-advance guard: only when the click moves DEEPER (a back-pointing
    // stepper/crumb link must land on the last real choice, never bounce).
    if (depthOf(target) > depthOf(current)) {
      var resolved = resolveForward(target);
      if (resolved.terminal) {
        if (window.clickableVehicleState) {
          window.clickableVehicleState.setVehicle(resolved.terminal.name, resolved.terminal.tag);
        }
        window.location = storeUrl(resolved.terminal.tag);
        return;
      }
      target = resolved.sel;
    }

    try {
      window.history.pushState({}, '', hrefFor(target));
    } catch (error) {
      window.location.href = link.getAttribute('href');
      return;
    }
    render(target);
  });

  window.addEventListener('popstate', function () {
    render(readSelection());
  });

  // Re-render on vehicle change (QuickFinder overlay, reset, garage pick).
  document.addEventListener('clickable:vehicle-changed', function () {
    render(readSelection());
  });
})();
